import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarClock, AlertTriangle, Sparkles, Flame, Phone, ArrowRight, Clock, Cake, Globe2, UserRound, Tag, Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Avatar } from "@/components/Avatar";
import { STATUS_META, STATUS_ORDER } from "@/lib/status";

const timeAgo = (iso) => {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400000);
  if (d > 0) return `${d}d ago`;
  const h = Math.floor(diff / 3600000);
  if (h > 0) return `${h}h ago`;
  const m = Math.floor(diff / 60000);
  return m > 0 ? `${m}m ago` : "Just now";
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtBday = (dob) => {
  if (!dob || dob.length < 10) return "";
  const m = parseInt(dob.slice(5, 7), 10), d = parseInt(dob.slice(8, 10), 10);
  return `${MONTHS[m - 1]} ${d}`;
};
const isToday = (dob) => {
  if (!dob || dob.length < 10) return false;
  const t = new Date();
  return dob.slice(5, 10) === `${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};

const CustomerRow = ({ c, onClick, overdue, showConsultant, currentUserId }) => {
  const showOwner = showConsultant || (c.consultant_id && currentUserId && c.consultant_id !== currentUserId);
  return (
  <button
    data-testid={`dashboard-customer-${c.id}`}
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/60 transition-colors text-left group"
  >
    <Avatar name={c.name || "Unknown"} size={36} />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-sm truncate">{c.name || "Unnamed lead"}</span>
        <StatusBadge status={c.status} />
      </div>
      <div className="text-xs text-muted-foreground truncate">
        {[c.company, c.country].filter(Boolean).join(" · ") || "No details"}
      </div>
      {showOwner && (
        <div data-testid={`row-owner-${c.id}`} className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5">
          <UserRound className="h-3 w-3" /> Owner: <span className="font-medium text-foreground">{c.consultant_name || "Unassigned"}</span>
        </div>
      )}
    </div>
    {overdue && c.next_followup_date && (
      <span className="text-xs font-medium text-destructive whitespace-nowrap">
        {c.next_followup_date}
      </span>
    )}
    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
  </button>
  );
};

const Panel = ({ title, icon: Icon, count, accent, children, testid }) => (
  <div data-testid={testid} className="bg-white border border-border rounded-lg flex flex-col overflow-hidden">
    <div className="flex items-center gap-2.5 px-4 py-3.5 border-b border-border">
      <div className="h-7 w-7 rounded-md flex items-center justify-center" style={{ background: `${accent}1a` }}>
        <Icon className="h-4 w-4" style={{ color: accent }} />
      </div>
      <h3 className="font-display font-semibold text-sm tracking-tight flex-1">{title}</h3>
      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${accent}1a`, color: accent }}>
        {count}
      </span>
    </div>
    <div className="divide-y divide-border max-h-80 overflow-y-auto flex-1">{children}</div>
  </div>
);

const Empty = ({ text }) => (
  <div className="px-4 py-8 text-center text-sm text-muted-foreground">{text}</div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const go = (id) => navigate(`/customers/${id}`);
  const isAdmin = user?.role === "admin";

  if (!data)
    return <div className="p-10 text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="p-8 max-w-[1400px] animate-in-up">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
        </p>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          {user?.role === "admin" ? "Command Center" : `Hey ${user?.name}`}
        </h1>
        <p className="text-muted-foreground mt-1">
          {data.overdue.length > 0
            ? `You have ${data.overdue.length} overdue follow-up${data.overdue.length > 1 ? "s" : ""} needing attention.`
            : "You're all caught up. Nice work."}
        </p>
      </div>

      {isAdmin && (data.my_followups_today || data.my_overdue) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Panel testid="panel-my-followups" title="My Follow-ups Today" icon={CalendarClock} accent="#0EA5E9" count={(data.my_followups_today || []).length}>
            {(data.my_followups_today || []).length ? data.my_followups_today.map((c) => <CustomerRow key={c.id} c={c} onClick={() => go(c.id)} />) : <Empty text="None assigned to you today" />}
          </Panel>
          <Panel testid="panel-my-overdue" title="My Overdue Follow-ups" icon={AlertTriangle} accent="#F43F5E" count={(data.my_overdue || []).length}>
            {(data.my_overdue || []).length ? data.my_overdue.map((c) => <CustomerRow key={c.id} c={c} onClick={() => go(c.id)} overdue />) : <Empty text="Nothing overdue for you" />}
          </Panel>
        </div>
      )}

      {isAdmin && (
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Organisation-wide</p>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Panel testid="panel-followups-today" title="Follow-ups Today" icon={CalendarClock} accent="#3B82F6" count={data.followups_today.length}>
          {data.followups_today.length ? data.followups_today.map((c) => <CustomerRow key={c.id} c={c} onClick={() => go(c.id)} showConsultant={isAdmin} currentUserId={user?.id} />) : <Empty text="No follow-ups scheduled today" />}
        </Panel>

        <Panel testid="panel-overdue" title="Overdue Follow-ups" icon={AlertTriangle} accent="#EF4444" count={data.overdue.length}>
          {data.overdue.length ? data.overdue.map((c) => <CustomerRow key={c.id} c={c} onClick={() => go(c.id)} overdue showConsultant={isAdmin} currentUserId={user?.id} />) : <Empty text="Nothing overdue — great job!" />}
        </Panel>

        <Panel testid="panel-hot-leads" title="Hot Leads" icon={Flame} accent="#F97316" count={data.hot_leads.length}>
          {data.hot_leads.length ? data.hot_leads.map((c) => <CustomerRow key={c.id} c={c} onClick={() => go(c.id)} showConsultant={isAdmin} currentUserId={user?.id} />) : <Empty text="No hot leads right now" />}
        </Panel>

        <Panel testid="panel-new-leads" title="New Leads Today" icon={Sparkles} accent="#8B5CF6" count={data.new_leads_today.length}>
          {data.new_leads_today.length ? data.new_leads_today.map((c) => <CustomerRow key={c.id} c={c} onClick={() => go(c.id)} showConsultant={isAdmin} currentUserId={user?.id} />) : <Empty text="No new leads captured today" />}
        </Panel>
      </div>

      {/* Birthdays This Month — visible to everyone */}
      <div data-testid="birthday-widget" className="bg-white border border-border rounded-lg overflow-hidden mb-6">
        <button
          data-testid="birthday-widget-header"
          onClick={() => navigate("/reports?tab=birthdays")}
          className="w-full flex items-center gap-2.5 px-4 py-3.5 border-b border-border hover:bg-secondary/50 transition-colors text-left"
        >
          <div className="h-7 w-7 rounded-md flex items-center justify-center" style={{ background: "#EC489915" }}>
            <Cake className="h-4 w-4" style={{ color: "#EC4899" }} />
          </div>
          <h3 className="font-display font-semibold text-sm tracking-tight flex-1">Birthdays This Month</h3>
          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#EC489915", color: "#EC4899" }}>
            {(data.birthdays_this_month || []).length}
          </span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </button>
        {(data.birthdays_this_month || []).length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
            {data.birthdays_this_month.map((b) => {
              const isTeam = b.contact_type === "Team Member";
              const canOpen = !b.is_user && (user?.role === "admin" || b.consultant_id === user?.id);
              const today = isToday(b.date_of_birth);
              return (
                <div
                  key={b.id}
                  data-testid={`birthday-card-${b.id}`}
                  className={`rounded-lg border p-4 ${today ? "border-pink-300 bg-pink-50/50" : "border-border"}`}
                >
                  <div className="flex items-center gap-2.5 mb-2">
                    <Avatar name={b.name || "?"} size={34} />
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                        {b.name || "Unnamed"}
                        {today && <span className="text-[10px] font-bold text-pink-600 bg-pink-100 px-1.5 py-0.5 rounded-full">TODAY 🎂</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{isTeam ? (b.position || "Team Member") : (b.company || "—")}</div>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5"><Cake className="h-3 w-3" /> {fmtBday(b.date_of_birth)}</div>
                    <div className="flex items-center gap-1.5"><Globe2 className="h-3 w-3" /> {b.country || "—"}</div>
                    <div className="flex items-center gap-1.5"><Tag className="h-3 w-3" /> {b.contact_type || "Customer"}</div>
                    {isTeam ? (
                      <div className="flex items-center gap-1.5"><Briefcase className="h-3 w-3" /> {b.position || "Team Member"}</div>
                    ) : (
                      <div className="flex items-center gap-1.5"><UserRound className="h-3 w-3" /> {b.consultant_name || "Unassigned"}</div>
                    )}
                  </div>
                  {canOpen ? (
                    <button
                      data-testid={`birthday-open-${b.id}`}
                      onClick={() => navigate(`/customers/${b.id}`)}
                      className="mt-3 w-full text-xs font-semibold rounded-md border border-border py-1.5 hover:bg-secondary transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Phone className="h-3 w-3" /> Open Contact
                    </button>
                  ) : b.is_user ? (
                    <div className="mt-3 text-[11px] text-center text-muted-foreground py-1.5">TSG Team Member</div>
                  ) : (
                    <div className="mt-3 text-[11px] text-center text-muted-foreground py-1.5">Managed by {b.consultant_name}</div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">No birthdays this month.</div>
        )}
      </div>

      {user?.role === "admin" && data.consultants && (
        <div data-testid="consultant-activity-table" className="bg-white border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3.5 border-b border-border">
            <h3 className="font-display font-semibold text-sm tracking-tight">Consultant Activity</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-4 py-2.5 font-medium">Consultant</th>
                  <th className="px-3 py-2.5 font-medium">Last Login</th>
                  <th className="px-3 py-2.5 font-medium">Last Activity</th>
                  <th className="px-3 py-2.5 font-medium text-center">Total</th>
                  {STATUS_ORDER.map((s) => (
                    <th key={s} className="px-3 py-2.5 font-medium text-center" style={{ color: STATUS_META[s].color }}>
                      {STATUS_META[s].label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.consultants.map((c) => {
                  const inactive = c.inactive_days != null && c.inactive_days >= 3;
                  return (
                    <tr key={c.id} data-testid={`consultant-row-${c.id}`} className="hover:bg-secondary/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={c.name} size={30} />
                          <div>
                            <div className="font-semibold flex items-center gap-1.5">
                              {c.name}
                              {inactive && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-destructive bg-destructive/10 px-1.5 py-0.5 rounded-full">
                                  <Clock className="h-2.5 w-2.5" /> {c.inactive_days}d idle
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">{c.country}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground text-xs">{timeAgo(c.last_login)}</td>
                      <td className="px-3 py-3 text-muted-foreground text-xs">{timeAgo(c.last_activity)}</td>
                      <td className="px-3 py-3 text-center font-semibold">{c.total}</td>
                      {STATUS_ORDER.map((s) => (
                        <td key={s} className="px-3 py-3 text-center">
                          <span className={c[s] ? "font-semibold" : "text-muted-foreground/40"}>{c[s]}</span>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
